import { useState } from 'react'
import {
    AlertCircle,
    CheckCircle2,
    Loader2,
    Printer,
    RefreshCw,
} from 'lucide-react'

import type {
    SalesOrder,
    SalesOrderItem,
    StockShortfallItem,
} from '@/lib/api/sales-order-api'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useStockCheck } from '@/hooks/use-sales-order-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    order: SalesOrder
    items: SalesOrderItem[]
}

// Print CSS: ẩn toàn bộ app, chỉ hiện .packaging-request-print-area khi user
// bấm "In phiếu yêu cầu đóng gói". Chữ to (16pt+) giống phiếu đóng hàng.
const PRINT_CSS = `
.packaging-request-print-area { display: none; }
@media print {
    body * { visibility: hidden !important; }
    .packaging-request-print-area, .packaging-request-print-area * { visibility: visible !important; }
    .packaging-request-print-area {
        display: block !important;
        position: fixed !important;
        left: 0; top: 0;
        width: 100%;
        padding: 20mm;
        background: white !important;
        color: black !important;
        font-size: 16pt;
        font-family: Arial, sans-serif;
    }
    .packaging-request-print-area h1 {
        font-size: 24pt;
        font-weight: 700;
        text-align: center;
        margin: 0 0 16pt 0;
        text-transform: uppercase;
    }
    .packaging-request-print-area .customer {
        font-size: 18pt;
        margin-bottom: 16pt;
    }
    .packaging-request-print-area table {
        border-collapse: collapse;
        width: 100%;
        font-size: 16pt;
    }
    .packaging-request-print-area th,
    .packaging-request-print-area td {
        border: 1.5pt solid #000;
        padding: 8pt 12pt;
    }
    .packaging-request-print-area th {
        background: #eee !important;
        font-weight: 700;
        text-align: left;
    }
    .packaging-request-print-area td.num { text-align: right; }
    .packaging-request-print-area td.center { text-align: center; }
}
`

export function AwaitingPackagingPanel({ order, items }: Props) {
    const { data, isFetching, isLoading, refetch } = useStockCheck(
        order.id,
        order.status === 'awaiting_packaging',
    )
    const [printing, setPrinting] = useState(false)
    const shortfalls: StockShortfallItem[] = data?.shortfalls ?? []
    const hasData = data !== undefined
    // Ready derive từ shortfalls để tránh trạng thái mâu thuẫn từ BE.
    const ready = hasData && shortfalls.length === 0

    const unitByProductId = items.reduce<Record<string, string>>((acc, it) => {
        if (it.product_id && !acc[it.product_id]) {
            acc[it.product_id] = it.unit ?? '—'
        }
        return acc
    }, {})

    const handlePrint = () => {
        // Guard tránh click double gây print 2 lần.
        if (printing) return
        setPrinting(true)
        // requestAnimationFrame để DOM có kịp render hidden print area
        // (setPrinting chỉ đổi state UI, không ảnh hưởng markup).
        requestAnimationFrame(() => {
            window.print()
            // Sau print dialog đóng, browser trả về event afterprint — reset flag.
            const done = () => {
                setPrinting(false)
                window.removeEventListener('afterprint', done)
            }
            window.addEventListener('afterprint', done)
        })
    }

    return (
        <>
            <style>{PRINT_CSS}</style>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                        {isLoading || !hasData ? (
                            <Loader2 className="size-5 animate-spin text-muted-foreground" />
                        ) : ready ? (
                            <CheckCircle2 className="size-5 text-green-600" />
                        ) : (
                            <AlertCircle className="size-5 text-amber-500" />
                        )}
                        {m.await_stock_check_title()}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handlePrint}
                            disabled={shortfalls.length === 0}
                        >
                            <Printer className="mr-2 size-4" />
                            {m.await_print_button()}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void refetch()}
                            disabled={isFetching}
                        >
                            {isFetching ? (
                                <Loader2 className="mr-2 size-4 animate-spin" />
                            ) : (
                                <RefreshCw className="mr-2 size-4" />
                            )}
                            {isFetching
                                ? m.await_stock_check_checking()
                                : m.await_stock_check_button()}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading || !hasData ? (
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Loader2 className="size-4 animate-spin" />
                            {m.await_stock_check_checking()}
                        </p>
                    ) : ready ? (
                        <p className="text-sm text-green-700">
                            {m.await_stock_check_ready()}
                        </p>
                    ) : (
                        <>
                            <p className="text-sm text-amber-700 mb-3">
                                {m.await_stock_check_short({
                                    count: shortfalls.length,
                                })}
                            </p>
                            <div className="overflow-x-auto rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                {m.await_table_col_product()}
                                            </TableHead>
                                            <TableHead className="w-16 text-center">
                                                {m.await_table_col_unit()}
                                            </TableHead>
                                            <TableHead className="w-24 text-right">
                                                {m.await_table_col_requested()}
                                            </TableHead>
                                            <TableHead className="w-24 text-right">
                                                {m.await_table_col_available()}
                                            </TableHead>
                                            <TableHead className="w-24 text-right font-semibold text-destructive">
                                                {m.await_table_col_missing()}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {shortfalls.map((sf) => (
                                            <TableRow key={sf.product_id}>
                                                <TableCell className="font-medium">
                                                    {sf.product_name}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {unitByProductId[
                                                        sf.product_id
                                                    ] ?? '—'}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {sf.requested}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums text-muted-foreground">
                                                    {sf.available}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums font-semibold text-destructive">
                                                    {sf.requested -
                                                        sf.available}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Hidden print-only area: chỉ visible khi @media print. Layout tối
                giản: heading + khách hàng + bảng SP thiếu (bỏ cột Tồn hiện có
                cho gọn — đội đóng gói chỉ cần biết số cần đóng thêm). */}
            <div className="packaging-request-print-area">
                <h1>{m.await_print_title()}</h1>
                <div className="customer">
                    {m.await_print_customer_label()}: {order.customer_name}
                </div>
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '10%', textAlign: 'center' }}>
                                {m.await_table_col_no()}
                            </th>
                            <th>{m.await_table_col_product()}</th>
                            <th style={{ width: '12%', textAlign: 'center' }}>
                                {m.await_table_col_unit()}
                            </th>
                            <th style={{ width: '18%', textAlign: 'right' }}>
                                {m.await_table_col_requested()}
                            </th>
                            <th style={{ width: '18%', textAlign: 'right' }}>
                                {m.await_table_col_missing()}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {shortfalls.map((sf, idx) => (
                            <tr key={sf.product_id}>
                                <td className="center">{idx + 1}</td>
                                <td>{sf.product_name}</td>
                                <td className="center">
                                    {unitByProductId[sf.product_id] ?? '—'}
                                </td>
                                <td className="num">{sf.requested}</td>
                                <td className="num">
                                    {sf.requested - sf.available}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    )
}
